import { Link, useNavigate, useLocation } from 'react-router-dom';
import dinoLogoFull from '@/assets/dino-logo-full.png';
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DinoAvatar } from '@/components/DinoAvatar';
import { MessageCircle } from 'lucide-react';
import { StreakDisplay } from '@/components/StreakDisplay';
import { AchievementNotificationBell } from '@/components/AchievementNotificationBell';
import { NotificationCenter } from '@/components/NotificationCenter';
import { SidebarTrigger } from '@/components/ui/sidebar';

export function Header() {
  const { user, session } = useAuth();
  const { getTotalUnreadCount } = useChats();
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState<{ username?: string; name?: string; image?: string; archetype?: string } | null>(null);
  
  const isOnboardingOrAuth = location.pathname.startsWith('/onboarding') || location.pathname === '/auth';

  // Load user profile data for navigation
  useEffect(() => {
    if (user) {
      const loadUserProfile = async () => {
        const { data } = await supabase
          .from('profiles')
          .select('username, name, image, archetype')
          .eq('id', user.id)
          .maybeSingle();
        
        setUserProfile(data);
      };
      
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user]);

  const handleViewPublicProfile = () => {
    if (userProfile?.username) {
      navigate(`/u/${userProfile.username}`);
    } else {
      navigate('/onboarding');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b border-secondary-light/30 bg-[hsl(var(--secondary))] shadow-soft text-secondary-foreground">
      <div className="container flex h-[var(--header-height)] items-center">
        {/* Sidebar Trigger - show on all screens except onboarding/auth */}
        {!isOnboardingOrAuth && (
          <SidebarTrigger 
            className="mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-secondary-light/50 dark:hover:bg-muted transition-colors" 
            aria-label="Toggle sidebar"
          />
        )}
        
        {/* Logo as Home Button */}
        <Link to="/" className="flex items-center">
          <img src={dinoLogoFull} alt="Dino - Home" className="h-10 sm:h-16 w-auto hover:opacity-80 transition-opacity" />
        </Link>

        <div className="flex flex-1 items-center justify-end space-x-2 md:space-x-4">
          {/* Streak Display */}
          {user && session && <StreakDisplay />}

          {/* Notification Center - combined on mobile */}
          {user && session && <NotificationCenter />}

          {/* Achievement Notifications - hidden on mobile, shown in More sheet */}
          {user && session && <div className="hidden md:block"><AchievementNotificationBell /></div>}

          {/* Messages Icon - hidden on mobile, in bottom nav */}
          {user && session && (
            <Button
              variant="ghost"
              size="sm"
              className="relative hidden md:flex"
              onClick={() => navigate('/inbox')}
            >
              <MessageCircle className="h-5 w-5" />
              {getTotalUnreadCount() > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 min-w-5 text-xs px-1 flex items-center justify-center"
                >
                  {getTotalUnreadCount() >= 9 ? '9+' : getTotalUnreadCount()}
                </Badge>
              )}
            </Button>
          )}

          {/* Mobile Search Button removed - Search is in mobile bottom nav */}


          {/* Safe Mode Banner (Dev only) */}
          {import.meta.env.DEV && import.meta.env.VITE_SAFE_MODE === 'true' && (
            <div className="px-2 py-1 bg-yellow-500 text-yellow-900 text-xs rounded font-medium">
              SAFE MODE
            </div>
          )}

          {user && session ? (
            <Button 
              variant="ghost" 
              className="relative h-10 w-10 rounded-full"
              onClick={handleViewPublicProfile}
            >
              <DinoAvatar
                src={userProfile?.image || user.user_metadata?.avatar_url || user.user_metadata?.image}
                name={userProfile?.name || user.user_metadata?.full_name || user.user_metadata?.name || user.email}
                size="sm"
              />
            </Button>
          ) : (
            <div className="flex items-center space-x-3">
              <Button variant="pill" asChild>
                <Link to="/auth?mode=signin">Sign In</Link>
              </Button>
              <Button variant="pillOutline" asChild>
                <Link to="/auth?mode=signup">Sign Up</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}